import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { getTankerPermits, generateTankerPermits, downloadTankerPermits } from '../../../models/tankerModel';

function TankerPermits() {
  const { user } = useAuth();
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    count: '',
    downloadFrom: ''
  });

  useEffect(() => {
    fetchPermits();
  }, []);

  const fetchPermits = async () => {
    try {
      setLoading(true);
      const data = await getTankerPermits();
      if (data.status === 1) {
        setPermits(data.data?.permits || []);
      } else {
        console.error('Failed to fetch permits:', data.message);
      }
    } catch (error) {
      console.error('Error fetching permits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePermit = async (e) => {
    e.preventDefault();
    
    if (!formData.count || formData.count <= 0) {
      alert('Please enter a valid number of permits to generate');
      return;
    }

    if (formData.count > 100) {
      alert('Cannot generate more than 100 permits at once');
      return;
    }
    
    try {
      setGenerating(true);
      const data = await generateTankerPermits({ permits: parseInt(formData.count) });
      if (data.status === 1) {
        alert(data.data?.message || `${formData.count} permits generated successfully!`);
        fetchPermits();
        setFormData({ ...formData, count: '' });
      } else {
        alert(data.message || 'Failed to generate permits');
      }
    } catch (error) {
      console.error('Error generating permits:', error);
      alert('Error generating permits');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPermits = async () => {
    try {
      // Add downloadFrom parameter if specified
      const params = formData.downloadFrom ? `?permits=${formData.downloadFrom}` : '';
      const data = await downloadTankerPermits(params);
      
      if (data.status === 1) {
        const permitCount = data.data?.count || 0;
        if (permitCount === 0) {
          alert('No permits found to download');
          return;
        }
        
        // TODO: Implement PDF generation on frontend or open download URL
        // For now, show success message
        alert(`${permitCount} permits ready for download. PDF generation will be implemented next.`);
        
        // Note: The legacy version generates PDF on backend using Dompdf
        // For React, we might want to either:
        // 1. Generate PDF on backend and return blob
        // 2. Use a frontend PDF library like jsPDF or react-pdf
      } else {
        alert(data.message || 'Failed to download permits');
      }
    } catch (error) {
      console.error('Error downloading permits:', error);
      alert('Error downloading permits');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Water Tanker Permits</h1>
                <p className="text-xs text-gray-500 mb-1">Generate and manage delivery permits for water tankers</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {permits.length} permit{permits.length !== 1 ? 's' : ''} total
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Generate New Permit Form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Generate Permits</h2>
            <p className="text-sm text-gray-600 mb-4">
              Generate multiple blank permits that can be allocated to tanker vehicles. Each permit will have a unique QR code.
            </p>
            <form onSubmit={handleGeneratePermit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Permits
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  max="100"
                  value={formData.count}
                  onChange={(e) => setFormData({...formData, count: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 10"
                />
                <p className="mt-1 text-xs text-gray-500">Enter the number of permits to generate (max 100)</p>
              </div>

              <button
                type="submit"
                disabled={generating}
                className={`w-full px-4 py-2 text-white rounded-md transition-colors ${
                  generating 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {generating ? 'Generating...' : 'Generate Permits'}
              </button>
            </form>

            <hr className="my-6" />

            <h2 className="text-lg font-medium text-gray-900 mb-4">Download Permits</h2>
            <p className="text-sm text-gray-600 mb-4">
              Download permits as PDF for printing. You can download all permits or start from a specific permit number.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Download from Permit #
              </label>
              <input
                type="number"
                min="0"
                value={formData.downloadFrom}
                onChange={(e) => setFormData({...formData, downloadFrom: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Leave empty to download all"
              />
              <p className="mt-1 text-xs text-gray-500">Optional: Start downloading from specific permit number</p>
            </div>
            <button
              onClick={handleDownloadPermits}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Download Permits PDF
            </button>
          </div>
        </div>

        {/* Permits List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Generated Permits</h2>
                <button
                  onClick={handleDownloadPermits}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Download All Permits
                </button>
              </div>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading permits...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Permit ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Allocated Vehicle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {permits.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                          No permits found. Generate your first permits using the form above.
                        </td>
                      </tr>
                    ) : (
                      permits.map((permit) => (
                        <tr key={permit.permit_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{permit.permit_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {permit.permit_created ? new Date(permit.permit_created * 1000).toLocaleDateString('en-ZA', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {permit.tanker_registration || 
                             (permit.tanker_permit_id ? `Permit ${permit.tanker_permit_id}` : 
                             <span className="text-gray-400 italic">Not allocated</span>)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              permit.tanker_permit_id 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {permit.tanker_permit_id ? 'Allocated' : 'Available'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => alert('QR Code view: Implementation pending')}
                                className="text-blue-600 hover:text-blue-900"
                                title="View QR Code"
                              >
                                View QR
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

export default TankerPermits; 